import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image } from 'lucide-react';
import VaultEntityCard from '../DataVault/VaultEntityCard';
import AddInvokeProfileModal from './AddInvokeProfileModal';
import { invokeProfilesApi, type InvokeProfile } from './invokeProfilesApi';
import { logInvokeProfiles } from './invokeProfilesLogger';

const InvokeProfiles: React.FC<{ isLight?: boolean; requestAdd?: number }> = ({ isLight = false, requestAdd = 0 }) => {
  const [list, setList] = useState<InvokeProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editProfile, setEditProfile] = useState<InvokeProfile | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await invokeProfilesApi.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoke profiles.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastAddTick = useRef(requestAdd);
  useEffect(() => {
    if (requestAdd === lastAddTick.current) return;
    lastAddTick.current = requestAdd;
    if (requestAdd < 1) return;
    setEditProfile(null);
    setShowModal(true);
  }, [requestAdd]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      setList(await invokeProfilesApi.remove(deleteId));
      setDeleteId(null);
    } catch (err) {
      logInvokeProfiles('error', 'list_delete_failed', { message: err instanceof Error ? err.message : String(err) });
      setError(err instanceof Error ? err.message : 'Failed to delete invoke profile.');
    } finally {
      setDeleting(false);
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';

  return (
    <div>
      {error ? <p className="mb-3 text-sm text-red-400">{error}</p> : null}
      {loading ? (
        <div className={`text-center py-10 text-sm ${muted}`}>Loading…</div>
      ) : list.length === 0 ? (
        <div className={`text-center py-10 text-sm ${muted}`}>No invoke profiles yet. Add one for image models.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((profile) => (
            <VaultEntityCard
              key={profile.id}
              isLight={isLight}
              title={profile.name}
              description={[
                profile.invoke === 'images_generations' ? 'Image generation' : 'Chat completions',
                profile.match.join(', ') || 'No pattern',
              ].join(' · ')}
              tag={profile.enabled ? 'Active' : 'Inactive'}
              icon={Image}
              iconClass={isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}
              inactive={!profile.enabled}
              onOpen={() => { setEditProfile(profile); setShowModal(true); }}
              onEdit={() => { setEditProfile(profile); setShowModal(true); }}
              onDelete={() => setDeleteId(profile.id)}
            />
          ))}
        </div>
      )}
      {showModal ? (
        <AddInvokeProfileModal
          onClose={() => { setShowModal(false); setEditProfile(null); }}
          onSaved={setList}
          initial={editProfile}
        />
      ) : null}
      {deleteId ? (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete invoke profile?</h3>
            <p className="text-gray-400 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDeleteId(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>Cancel</button>
              <button type="button" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InvokeProfiles;
